/**
 * AIS Vessel Tracking data layer - checkbox, date range, vessel list,
 * map style (points/trails/heatmap), filters, fleet summary.
 * Activation: include vessel-tracking in initialLayers. Other UI: component_default_values.
 */
import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import type { RegistryComponentProps } from '../../../pages/registry/componentRegistry';
import { useApi } from '../../../api/api';
import { isScallopVessel } from '../../../utils/scallopVessels';
import BetaChip from '../../../components/common/BetaChip/BetaChip';
import { useDataLayer } from '../hooks/useDataLayer';

/** Layout component_default_values for vessel-tracking (flat or nested under default). */
interface VesselTrackingDefaultValues {
  default?: {
    date_start?: string;
    date_end?: string;
    map_style?: string;
    selected_vessel?: string;
    fishing_only?: boolean;
    hide_in_port?: boolean;
  };
  date_start?: string;
  date_end?: string;
  map_style?: string;
  selected_vessel?: string;
  fishing_only?: boolean;
  hide_in_port?: boolean;
}

function getVesselDefault<T>(
  dv: VesselTrackingDefaultValues | null | undefined,
  key: keyof NonNullable<VesselTrackingDefaultValues['default']>
): T | undefined {
  if (!dv) return undefined;
  const v = (dv as Record<string, unknown>)[key] ?? (dv.default as Record<string, unknown>)?.[key];
  return v as T | undefined;
}

/** MapController instance from MapContext (MapContext.jsx has no TS types). */
interface MapControllerLike {
  isReady: () => boolean;
  isLayerActive: (slug: string) => boolean;
  addLayer: (slug: string) => Promise<void>;
  removeLayer: (slug: string, force?: boolean) => Promise<void>;
  setAISData: (data: unknown[], displayOptions?: { mapStyle?: string }) => void;
}

interface AISVessel {
  mmsi: string;
  vessel_name?: string;
  [key: string]: unknown;
}

interface AISFleetSummary {
  total_fishing_hours?: number;
  total_observations?: number;
  [key: string]: unknown;
}

export default function VesselTrackingDataLayer(
  props: RegistryComponentProps
): React.ReactElement {
  const { getAISPositions, getAISVesselList, getAISFleetSummary } = useApi();
  const { slug, isReady, isActive, setActive, mapController, data } = useDataLayer(props) as {
    slug: string;
    isReady: boolean;
    isActive: boolean;
    setActive: (v: boolean) => Promise<void>;
    mapController: MapControllerLike | null;
    data: Record<string, unknown>;
  };
  const dv = data as VesselTrackingDefaultValues | null | undefined;
  const syncedFromLayoutRef = useRef(false);

  // When layer is in layout, mapController has it active but our isActive may not have synced yet.
  useLayoutEffect(() => {
    if (syncedFromLayoutRef.current || !setActive || !mapController?.isReady()) return;
    if (mapController.isLayerActive(slug) && !isActive) {
      setActive(true);
      syncedFromLayoutRef.current = true;
    }
  }, [slug, isActive, setActive, mapController]);

  const [aisDataLoading, setAisDataLoading] = useState(false);
  const [aisVessels, setAisVessels] = useState<AISVessel[]>([]);
  const [aisSelectedVessel, setAisSelectedVessel] = useState(() => {
    const v = getVesselDefault<string>(dv, 'selected_vessel');
    return v != null && v !== '' ? String(v) : '';
  });
  const [aisDateRange, setAisDateRange] = useState(() => {
    const start = getVesselDefault<string>(dv, 'date_start') ?? '2024-03-01';
    const end = getVesselDefault<string>(dv, 'date_end') ?? '2024-07-31';
    return { start, end };
  });
  const [aisMapStyle, setAisMapStyle] = useState(() => {
    const v = getVesselDefault<string>(dv, 'map_style');
    const valid = ['scatterPoints', 'trajectoryLines', 'heatMap'];
    return v && valid.includes(v) ? v : 'scatterPoints';
  });
  const [aisFishingOnly, setAisFishingOnly] = useState(() => getVesselDefault<boolean>(dv, 'fishing_only') === true);
  const [aisHideInPort, setAisHideInPort] = useState(() => getVesselDefault<boolean>(dv, 'hide_in_port') === true);
  const [aisFleetSummary, setAisFleetSummary] = useState<AISFleetSummary | null>(null);
  const [aisFilteredVesselCount, setAisFilteredVesselCount] = useState<number | null>(null);

  // Sync from layout defaults when they change (e.g. page navigation)
  const defaultDateStart = getVesselDefault<string>(dv, 'date_start');
  const defaultDateEnd = getVesselDefault<string>(dv, 'date_end');
  const defaultMapStyle = getVesselDefault<string>(dv, 'map_style');
  const defaultSelectedVessel = getVesselDefault<string>(dv, 'selected_vessel');
  const defaultFishingOnly = getVesselDefault<boolean>(dv, 'fishing_only');
  const defaultHideInPort = getVesselDefault<boolean>(dv, 'hide_in_port');
  const lastAppliedDefaultsRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${defaultDateStart ?? ''}|${defaultDateEnd ?? ''}|${defaultMapStyle ?? ''}|${defaultSelectedVessel ?? ''}|${defaultFishingOnly}|${defaultHideInPort}`;
    if (key === lastAppliedDefaultsRef.current) return;
    lastAppliedDefaultsRef.current = key;
    if ((defaultDateStart != null && defaultDateStart !== '') || (defaultDateEnd != null && defaultDateEnd !== '')) {
      setAisDateRange((prev) => ({
        start: defaultDateStart ?? prev.start,
        end: defaultDateEnd ?? prev.end,
      }));
    }
    if (defaultMapStyle && ['scatterPoints', 'trajectoryLines', 'heatMap'].includes(defaultMapStyle)) setAisMapStyle(defaultMapStyle);
    if (defaultSelectedVessel != null) setAisSelectedVessel(String(defaultSelectedVessel));
    if (defaultFishingOnly === true || defaultFishingOnly === false) setAisFishingOnly(defaultFishingOnly === true);
    if (defaultHideInPort === true || defaultHideInPort === false) setAisHideInPort(defaultHideInPort === true);
  }, [defaultDateStart, defaultDateEnd, defaultMapStyle, defaultSelectedVessel, defaultFishingOnly, defaultHideInPort]);


  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (isActive) {
      mapController.addLayer('scallop-areas');
      return () => {
        mapController.removeLayer('scallop-areas');
      };
    }
    setAisFilteredVesselCount(null);
  }, [isActive, mapController]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (isActive && aisVessels.length === 0) {
      const fetchVesselList = async () => {
        try {
          const response = await getAISVesselList();
          const data = (response as { data?: AISVessel[] }).data ?? [];
          const scallopVessels = data.filter((v) => isScallopVessel(v.vessel_name ?? ''));
          setAisVessels(scallopVessels);
        } catch (error) {
          console.error('Error fetching AIS vessel list:', error);
          setAisVessels([]);
        }
      };
      fetchVesselList();
    }
  }, [isActive, mapController, getAISVesselList, aisVessels.length]);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (isActive && !aisFleetSummary) {
      const fetchFleetSummary = async () => {
        try {
          const response = await getAISFleetSummary();
          setAisFleetSummary((response as { data?: AISFleetSummary }).data ?? null);
        } catch (error) {
          console.error('Error fetching AIS fleet summary:', error);
        }
      };
      fetchFleetSummary();
    }
  }, [isActive, mapController, getAISFleetSummary, aisFleetSummary]);

  useEffect(() => {
    const handleFilteredCount = (event: CustomEvent<{ uniqueVessels: number }>) => {
      const { uniqueVessels } = event.detail;
      setAisFilteredVesselCount(uniqueVessels);
    };
    window.addEventListener('aisFilteredCount', handleFilteredCount as EventListener);
    return () => window.removeEventListener('aisFilteredCount', handleFilteredCount as EventListener);
  }, []);

  useEffect(() => {
    if (!mapController?.isReady()) return;
    if (isActive) {
      const fetchAISPositions = async () => {
        setAisDataLoading(true);
        try {
          const params: Record<string, unknown> = {
            startDate: aisDateRange.start,
            endDate: aisDateRange.end,
            scallopOnly: true,
            limit: 30000,
          };
          if (aisSelectedVessel) params.mmsi = aisSelectedVessel;
          if (aisFishingOnly) params.fishingOnly = true;
          if (aisHideInPort) params.atSeaOnly = true;

          const response = await getAISPositions(params);
          const data = (response as { data?: unknown[] }).data ?? [];
          mapController.setAISData(data, { mapStyle: aisMapStyle });
        } catch (error) {
          console.error('Error fetching AIS positions:', error);
          mapController.setAISData([]);
        } finally {
          setAisDataLoading(false);
        }
      };
      fetchAISPositions();
    }
  }, [isActive, mapController, getAISPositions, aisSelectedVessel, aisDateRange, aisFishingOnly, aisHideInPort, aisMapStyle]);

  return (
    <div className="control-section">
      <div className="section-title">
        <span className="section-icon">AIS Vessel Tracking <BetaChip /></span>
      </div>

      <div
        style={{
          fontSize: '12px',
          color: '#6c757d',
          marginBottom: '12px',
          fontStyle: 'italic',
        }}
      >
        Scallop fleet AIS positions by date range and vessel
      </div>
      <div className="form-group">
        <label
          className="checkbox-label"
          style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const checked = e.target.checked;
              if (checked) setActive(true);
              else {
                setActive(false);
                setAisFilteredVesselCount(null);
              }
            }}
            disabled={!isReady}
            style={{ marginRight: '8px' }}
          />
          <span>Show Scallop Fleet Positions {isActive && aisFilteredVesselCount != null && `(${aisFilteredVesselCount} vessels)`}</span>
        </label>
      </div>

      {isActive && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontWeight: '500' }}>
              VISUALIZATION
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { value: 'scatterPoints', label: 'Points', icon: '●' },
                { value: 'trajectoryLines', label: 'Trails', icon: '〰' },
                { value: 'heatMap', label: 'Heatmap', icon: '◐' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setAisMapStyle(option.value);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    backgroundColor: aisMapStyle === option.value ? '#A78BFA' : '#f3f4f6',
                    color: aisMapStyle === option.value ? 'white' : '#4b5563',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <span style={{ fontSize: '14px' }}>{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px', fontWeight: '500' }}>
              QUICK FILTERS
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setAisFishingOnly(true);
                  setAisHideInPort(true);
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  backgroundColor: (aisFishingOnly && aisHideInPort) ? '#FF69B4' : '#fff0f7',
                  color: (aisFishingOnly && aisHideInPort) ? 'white' : '#FF69B4',
                  border: '1px solid #FF99CC',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                Fishing Only
              </button>
              <button
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setAisFishingOnly(false);
                  setAisHideInPort(true);
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  backgroundColor: (!aisFishingOnly && aisHideInPort) ? '#A855F7' : '#f5f0ff',
                  color: (!aisFishingOnly && aisHideInPort) ? 'white' : '#A855F7',
                  border: '1px solid #C084FC',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                At Sea
              </button>
              <button
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setAisFishingOnly(false);
                  setAisHideInPort(false);
                }}
                style={{
                  padding: '6px 12px',
                  fontSize: '11px',
                  backgroundColor: (!aisFishingOnly && !aisHideInPort) ? '#64748B' : '#f8fafc',
                  color: (!aisFishingOnly && !aisHideInPort) ? 'white' : '#64748B',
                  border: '1px solid #cbd5e1',
                  borderRadius: '16px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
              >
                All Activity
              </button>
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '10px',
              marginBottom: '12px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ marginBottom: '8px' }}>
              <select
                value={aisSelectedVessel}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAisSelectedVessel(e.target.value)}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  fontSize: '12px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                <option value="">All Scallop Vessels ({aisFilteredVesselCount ?? '...'} with data)</option>
                {aisVessels.map((vessel) => (
                  <option key={vessel.mmsi} value={vessel.mmsi}>
                    {vessel.vessel_name}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input
                type="date"
                value={aisDateRange.start}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAisDateRange((prev) => ({ ...prev, start: e.target.value }))
                }
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                }}
              />
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>to</span>
              <input
                type="date"
                value={aisDateRange.end}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setAisDateRange((prev) => ({ ...prev, end: e.target.value }))
                }
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  backgroundColor: 'white',
                  color: '#1f2937',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                }}
              />
            </div>
          </div>

          <div
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              padding: '10px 12px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-around' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#FF69B4' }} />
                <span style={{ fontSize: '10px', color: '#4b5563' }}>Fishing</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#A855F7' }} />
                <span style={{ fontSize: '10px', color: '#4b5563' }}>Transit</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#FDE68A' }} />
                <span style={{ fontSize: '10px', color: '#4b5563' }}>In Port</span>
              </div>
            </div>
          </div>

          {aisFleetSummary && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '10px',
                padding: '8px 0',
                borderTop: '1px solid #e5e7eb',
                fontSize: '10px',
                color: '#6b7280',
              }}
            >
              <span>{aisFleetSummary.total_fishing_hours?.toLocaleString()} fishing hrs</span>
              <span>{aisFleetSummary.total_observations?.toLocaleString()} observations</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
